import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { Button, Grid, Theme, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { GroupFieldConfiguration } from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { PRIMARY_BUTTON_STYLE } from '../../../data/constants';
import FieldLabel from '../../fields/FieldLabel';
import { GROUP_FIELD } from '../data/constants';
import { DefaultValuesType } from '../types';
import { Form } from '../utils';
import getEmptyGroupFieldValues from '../utils/getEmptyGroupFieldValues';

interface Props {
	form: string;
	groupField: GroupFieldConfiguration;
	defaultValues: DefaultValuesType;
	control: Control;
	authData: AuthData;
	productName: string;
	configVersion: string;
	effectiveDate?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
	accordion: {
		border: `1px solid ${theme.palette.divider}`,
		'&:not(:last-child)': {
			borderBottom: 0,
		},
		'&:before': {
			display: 'none',
		},
	},
	summary: {
		backgroundColor: 'rgba(0, 0, 0, .03)',
		flexDirection: 'row-reverse',
		'& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
			transform: 'rotate(90deg)',
		},
		'& .MuiAccordionSummary-content': {
			marginLeft: theme.spacing(1),
		},
	},
	content: {
		alignItems: 'center',
	},
	details: {
		padding: theme.spacing(2),
		borderTop: '1px solid rgba(0, 0, 0, .125)',
	},
}));

const GroupField = ({
	form,
	defaultValues,
	groupField,
	control,
	authData,
	configVersion,
	productName,
	effectiveDate,
}: Props) => {
	const classes = useStyles();
	const { fields, remove, append } = useFieldArray({
		control,
		name: form + '.' + groupField.name,
	});
	const { disabled, repeatable, optional, title } = groupField;
	const groupFields = fields.map((field, index) => {
		return (
			// According to the doc: The field.id (and not index) must be added as the component key to prevent re-renders breaking the fields:
			<Grid item xs={12} key={field.id}>
				<Accordion
					disableGutters
					elevation={0}
					square
					classes={{ root: classes.accordion }}
				>
					<AccordionSummary
						expandIcon={
							<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />
						}
						classes={{ root: classes.summary, content: classes.content }}
					>
						<Typography variant='body2' sx={{ flexGrow: 1 }}>
							{groupField.title + ' #' + (index + 1)}
						</Typography>
						<Button
							variant='text'
							size='small'
							onClick={() => {
								remove(index);
							}}
							disabled={disabled}
						>
							{GROUP_FIELD.delete}
						</Button>
					</AccordionSummary>
					<AccordionDetails classes={{ root: classes.details }}>
						<Grid container spacing={2}>
							{Form(
								form,
								groupField.fields,
								[],
								control,
								authData,
								productName,
								configVersion,
								fields[index],
								groupField.name + '.' + index,
								disabled,
								effectiveDate,
							)}
						</Grid>
					</AccordionDetails>
				</Accordion>
			</Grid>
		);
	});

	return (
		<Grid item xs={12} sx={{ mb: 2 }}>
			<Grid container spacing={2}>
				<Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
					<FieldLabel required={!optional} title={title || ''} />
					{!disabled && (repeatable || fields.length === 0) && (
						<Button
							color='primary'
							onClick={() => {
								append(getEmptyGroupFieldValues(groupField));
							}}
							disabled={disabled}
							sx={{
								...PRIMARY_BUTTON_STYLE,
								'&:hover': {
									...PRIMARY_BUTTON_STYLE,
								},
							}}
						>
							{GROUP_FIELD.add}
						</Button>
					)}
					{/* // TODO: implement Expand All */}
				</Grid>
				<Grid item xs={12}>
					{groupFields}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default GroupField;
